import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, AlertCircle, CalendarDays, CalendarCheck, ChevronDown } from 'lucide-react';
import api from '../api';
import { ListSkeleton, EmptyState } from '../components/ui';

const priorityColor = (p) => {
  if (p === 'alta') return 'var(--red)';
  if (p === 'media') return 'var(--yellow)';
  return 'var(--green)';
};

const dateChipStyle = (task) => {
  if (task.scaduto) return { background: 'var(--red-dim)', color: 'var(--red)' };
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(task.data_scadenza); d.setHours(0,0,0,0);
  const diff = Math.floor((d - today) / 86400000);
  if (diff <= 1) return { background: 'var(--yellow-dim)', color: 'var(--yellow)' };
  return { background: 'var(--bg3)', color: 'var(--text-muted)' };
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const getISOWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  d.setHours(0,0,0,0);
  return d;
};

const weekLabel = (weekStart) => {
  const d = new Date(weekStart);
  return 'Settimana ' + d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
};

export default function Scadenzario() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [giorni, setGiorni] = useState(30);
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroAssegnato, setFiltroAssegnato] = useState('');

  useEffect(() => {
    setLoading(true);
    let url = `/scadenzario?giorni=${giorni}`;
    if (filtroStato) url += `&stato=${filtroStato}`;
    api.get(url)
      .then(r => setTasks(r.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [giorni, filtroStato]);

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      const clienteMatch = filtroCliente
        ? ((t.azienda || '') + ' ' + (t.cliente_nome || '')).toLowerCase().includes(filtroCliente.toLowerCase())
        : true;
      const assegnatoMatch = filtroAssegnato
        ? (t.assegnato_nome || '').toLowerCase().includes(filtroAssegnato.toLowerCase())
        : true;
      return clienteMatch && assegnatoMatch;
    });
  }, [tasks, filtroCliente, filtroAssegnato]);

  const stats = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const in7 = new Date(today); in7.setDate(today.getDate() + 7);
    let scadute = 0, oggi = 0, prossimi7 = 0;
    filtered.forEach(t => {
      if (t.scaduto) { scadute++; return; }
      const d = new Date(t.data_scadenza); d.setHours(0,0,0,0);
      if (d.getTime() === today.getTime()) { oggi++; }
      else if (d > today && d <= in7) { prossimi7++; }
    });
    return { scadute, oggi, prossimi7 };
  }, [filtered]);

  const groups = useMemo(() => {
    const scadute = filtered.filter(t => t.scaduto).sort((a,b) => new Date(a.data_scadenza) - new Date(b.data_scadenza));
    const future = filtered.filter(t => !t.scaduto).sort((a,b) => new Date(a.data_scadenza) - new Date(b.data_scadenza));
    const weekMap = {};
    future.forEach(t => {
      const ws = getISOWeekStart(t.data_scadenza).toISOString();
      if (!weekMap[ws]) weekMap[ws] = [];
      weekMap[ws].push(t);
    });
    const result = [];
    if (scadute.length) result.push({ key: 'scadute', label: 'Scadute', tasks: scadute, isScadute: true });
    Object.keys(weekMap).sort().forEach(ws => {
      result.push({ key: ws, label: weekLabel(ws), tasks: weekMap[ws], isScadute: false });
    });
    return result;
  }, [filtered]);

  return (
    <div className="eden-page eden-page--narrow">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <Calendar size={28} color="var(--cyan)" />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>Scadenzario</h1>
      </div>
      <p style={{ margin: '0 0 24px 40px', color: 'var(--text-muted)', fontSize: 14 }}>Tutte le task con scadenza</p>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <select
            className="eden-select"
            value={giorni}
            onChange={e => setGiorni(Number(e.target.value))}
            style={{ paddingRight: 32 }}
          >
            {[7, 14, 30, 60, 90].map(g => (
              <option key={g} value={g}>Prossimi {g} giorni</option>
            ))}
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
        </div>
        <div style={{ position: 'relative' }}>
          <select
            className="eden-select"
            value={filtroStato}
            onChange={e => setFiltroStato(e.target.value)}
            style={{ paddingRight: 32 }}
          >
            <option value="">Aperti (tutti)</option>
            <option value="da_fare">Da fare</option>
            <option value="in_corso">In corso</option>
          </select>
          <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
        </div>
        <input
          className="eden-input"
          placeholder="Filtra per cliente..."
          value={filtroCliente}
          onChange={e => setFiltroCliente(e.target.value)}
          style={{ minWidth: 180 }}
        />
        <input
          className="eden-input"
          placeholder="Filtra per assegnato..."
          value={filtroAssegnato}
          onChange={e => setFiltroAssegnato(e.target.value)}
          style={{ minWidth: 180 }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--red-dim)', border: '1px solid var(--red)', borderRadius: 8, padding: '8px 16px' }}>
          <AlertCircle size={16} color="var(--red)" />
          <span style={{ color: 'var(--red)', fontWeight: 700, fontSize: 18 }}>{stats.scadute}</span>
          <span style={{ color: 'var(--red)', fontSize: 13 }}>scadute</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--yellow-dim)', border: '1px solid var(--yellow)', borderRadius: 8, padding: '8px 16px' }}>
          <Clock size={16} color="var(--yellow)" />
          <span style={{ color: 'var(--yellow)', fontWeight: 700, fontSize: 18 }}>{stats.oggi}</span>
          <span style={{ color: 'var(--yellow)', fontSize: 13 }}>oggi</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--cyan-dim)', border: '1px solid var(--cyan)', borderRadius: 8, padding: '8px 16px' }}>
          <CalendarDays size={16} color="var(--cyan)" />
          <span style={{ color: 'var(--cyan)', fontWeight: 700, fontSize: 18 }}>{stats.prossimi7}</span>
          <span style={{ color: 'var(--cyan)', fontSize: 13 }}>nei prossimi 7gg</span>
        </div>
      </div>

      {loading ? (
        <ListSkeleton rows={6} />
      ) : groups.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="Nessuna scadenza" hint="Non ci sono task con scadenza nel periodo selezionato." />
      ) : (
        groups.map(group => (
          <div key={group.key} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{
                fontWeight: 700,
                fontSize: 13,
                color: group.isScadute ? 'var(--red)' : 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {group.label}
              </span>
              <span style={{
                background: group.isScadute ? 'var(--red-dim)' : 'var(--bg3)',
                color: group.isScadute ? 'var(--red)' : 'var(--text-muted)',
                borderRadius: 12,
                padding: '1px 8px',
                fontSize: 12,
                fontWeight: 600
              }}>
                {group.tasks.length}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {group.tasks.map(task => (
                <div
                  key={task.id}
                  className="eden-card"
                  onClick={() => navigate(`/task?open=${task.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    cursor: 'pointer',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-h)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: priorityColor(task.priorita),
                    flexShrink: 0
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14, marginBottom: 2 }}>
                      {task.titolo}
                    </div>
                    <a
                      href={`/clienti/${task.cliente_id}`}
                      onClick={e => { e.stopPropagation(); navigate(`/clienti/${task.cliente_id}`); }}
                      style={{
                        color: 'var(--cyan)',
                        fontSize: 12,
                        textDecoration: 'none',
                        maxWidth: 200,
                        display: 'inline-block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {task.azienda || task.cliente_nome || '—'}
                    </a>
                  </div>
                  {task.assegnato_nome && (
                    <span style={{
                      background: 'var(--bg3)',
                      color: 'var(--text-muted)',
                      borderRadius: 12,
                      padding: '2px 10px',
                      fontSize: 12,
                      fontWeight: 500,
                      whiteSpace: 'nowrap'
                    }}>
                      {task.assegnato_nome}
                    </span>
                  )}
                  <span style={{
                    borderRadius: 6,
                    padding: '3px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    ...dateChipStyle(task)
                  }}>
                    {formatDate(task.data_scadenza)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
